import * as React from 'react';
import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ColumnDef,
  DisplayColumnDef,
  FilterMeta,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowData,
  Table,
  TableOptions,
} from '@tanstack/table-core';

import {fuzzyMatch} from 'common/util/fuzzy-match';
import {numericSortOrder, rowSortComparator} from 'common/report/util/sorting/table-sorting';
import {SortOrder} from 'common/types/reports/bookmark';
import {RawNumberCellType, TextCellType} from 'common/components/table-react/cell-types';

import {AbridgedCellContext, AbridgedColumnDef, BuiltInCellType, CellType, CellTypeDef, ColumnDefMeta} from './types';
import {getCellText} from './utils';

function getCellTextFromRow(row: Row<any>, columnId: string): string {
  // note: unfortunately it seems like this private method `row._getAllCellsByColumnId()` is the only practical way to get the cell efficiently
  const cell = row._getAllCellsByColumnId()[columnId];
  return getCellText(row.getValue(columnId), cell?.column?.columnDef);
}

function globalFuzzyMatchFilter<TData extends RowData>(
  row: Row<TData>,
  columnId: string,
  filterValue: any,
  addMeta: (meta: FilterMeta) => void,
): boolean {
  const cellText = getCellTextFromRow(row, columnId);
  const matchResult = fuzzyMatch(cellText, filterValue);
  if (matchResult) {
    addMeta({matchResult} as FilterMeta);
    return true;
  }
  return false;
}

function getCellSortableValueFromRow(row: Row<any>, columnId: string): string | number | boolean {
  // note: unfortunately it seems like this private method `row._getAllCellsByColumnId()` is the only practical way to get the cell efficiently
  const cell = row._getAllCellsByColumnId()[columnId];
  if (cell) {
    const columnMetadata: ColumnDefMeta | null | undefined = cell?.column?.columnDef?.meta;
    if (columnMetadata?.sortRankTransform) {
      return columnMetadata.sortRankTransform(row.getValue(columnId), cell.getContext());
    }
  }

  const value = row.getValue(columnId);
  if ([`string`, `number`, `boolean`].includes(typeof value)) {
    return value as any;
  }

  return getCellTextFromRow(row, columnId);
}

function oldTableRowSort<TData>(rowA: Row<TData>, rowB: Row<TData>, columnId: string): -1 | 0 | 1 {
  const rank = rowSortComparator(
    {v: getCellSortableValueFromRow(rowA, columnId)} as any,
    {v: getCellSortableValueFromRow(rowB, columnId)} as any,
    {sortOrder: numericSortOrder(SortOrder.Ascending), sortField: `v`},
  );
  if (rank > 0) {
    return 1;
  }
  if (rank < 0) {
    return -1;
  }
  return 0;
}

function getCellTypeDef(
  cellType: string,
  customCellTypes: Record<string, CellTypeDef<any, any>> | null | undefined,
): CellTypeDef<unknown, any> {
  const custom = customCellTypes?.[cellType];
  if (custom) {
    return custom;
  }

  // eslint-disable-next-line sonarjs/no-small-switch
  switch (cellType) {
    case `text`:
      return TextCellType;
    case `rawnumber`:
      return RawNumberCellType;
    default:
      throw new Error(`Unknown cell type: ${cellType}`);
  }
}

export interface TableReactHookOptions<
  TData extends RowData,
  CustomCellTypes extends Record<string, CellTypeDef<TData, any>> = Record<string, CellTypeDef<TData, any>>,
> {
  columns: Array<AbridgedColumnDef<TData, BuiltInCellType | CellType<TData, CustomCellTypes>>>;
  data: TData[];
  customCellTypes?: CustomCellTypes;
  defaultGlobalFilter?: string;
  defaultSorting?: SortingState;
  enableRowSelection?: boolean;
  /**
   * Controls table's internal columnFilter state. If this value is left unset, table.getState().columnFilters will be undefined,
   * and filterFn field of column will not be executed. If a column is filterable in a table, this is required.
   *
   */
  initializedColumnFilterState?: ColumnFiltersState;
  /** this is what the column filter state defaults are -- IE: if reset filters were clicked */
  defaultColumnFilterState?: ColumnFiltersState;
  disableColumnHeaders?: boolean;
}

export function useTable<TData extends RowData, CustomCellTypes extends Record<string, CellTypeDef<TData, any>>>(
  options: TableReactHookOptions<TData, CustomCellTypes>,
) {
  const [globalFilter, setGlobalFilter] = React.useState(options.defaultGlobalFilter);
  const [sorting, setSorting] = React.useState<SortingState>(options.defaultSorting ?? []);

  const columnHelper = createColumnHelper<TData>();
  const columns = options.columns
    .map((column): ColumnDef<TData> => {
      if (`accessor` in column) {
        const meta: ColumnDefMeta = {
          ...getCellTypeDef(column.cellType, options.customCellTypes),
          fixedSize: column.fixedSize,
          columnNoFilterLabel: column.columnNoFilterLabel,
          headerStyleOverrides: column.headerStyleOverrides,
          columnSelectionParams: column.columnSelectionParams,
        };
        return columnHelper.accessor(column.accessor, {
          id: column.id,
          cell: (info) => {
            const localMeta: ColumnDefMeta | undefined = info.column.columnDef.meta;
            const cellContext: AbridgedCellContext<TData, any> = {
              cellContext: info,
              getFilterMatch() {
                return this.cellContext.row.columnFiltersMeta[this.cellContext.column.id]?.matchResult;
              },
            };
            return localMeta!.template!(info.getValue(), cellContext, column.templateParams);
          },
          header: column.headerTitle,
          size: column.fractionSize,
          enableGlobalFilter: column.enableGlobalFilter,
          enableSorting: column.enableSorting,
          sortingFn: column.sortingFn ?? (`oldTable` as any),
          filterFn: column.filterFn,
          enableColumnFilter: column.enableColumnFilter,
          meta,
        });
      }
      if (`cell` in column) {
        const meta: ColumnDefMeta = {
          fixedSize: column.fixedSize,
          headerStyleOverrides: column.headerStyleOverrides,
          columnSelectionParams: column.columnSelectionParams,
        };
        return columnHelper.display({
          id: column.id,
          cell: column.cell as DisplayColumnDef<TData>[`cell`],
          header: column.headerTitle,
          size: column.fractionSize,
          meta,
        });
      }
      throw new Error(`Invalid table column definition`);
    })
    .filter((column) => column !== null);

  const tanstackOptions: TableOptions<TData> = {
    columns,
    data: options.data,
    getCoreRowModel: getCoreRowModel<TData>(),
    getFilteredRowModel: getFilteredRowModel<TData>(),
    getSortedRowModel: getSortedRowModel<TData>(),
    getFacetedRowModel: getFacetedRowModel<TData>(),
    getFacetedUniqueValues: getFacetedUniqueValues<TData>(),
    enableFilters: true,
    filterFns: {
      // TODO: properly declare the FilterFns typedef via declaration merging (https://tanstack.com/table/latest/docs/api/features/global-filtering#filterfns)
      fuzzy: globalFuzzyMatchFilter,
    },
    sortingFns: {
      // TODO: properly declare the FilterFns typedef via declaration merging (https://tanstack.com/table/v8/docs/api/features/sorting/#sortingfns)
      oldTable: oldTableRowSort,
    },
    state: {
      globalFilter,
      sorting,
      columnFilters: options.initializedColumnFilterState, // this will take precedence over initialState.columnFilters
    },
    initialState: {
      columnFilters: options.defaultColumnFilterState,
      columnVisibility: {
        ...options.columns.reduce(
          (acc, column) => {
            if (column.id && column?.isHiddenColumnForFilter) {
              acc[column.id] = false;
            }
            return acc;
          },
          {} as Record<string, boolean>,
        ),
      },
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    globalFilterFn: `fuzzy` as any,
    enableRowSelection: options.enableRowSelection,
  };
  return useReactTable(tanstackOptions);
}

export const TableGroupContext = React.createContext<Nullable<Table<any>>>(null);

export function useTableGroupContext<TData extends RowData>(): Table<TData> {
  const table = React.useContext(TableGroupContext);
  if (!table) {
    throw new Error(`No table group found`);
  }
  return table;
}
